/**
 * P3-6 离职转移日志
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { PageHeader, HomeCard, Pill, TableWrap, Avatar } from '../../components/ui';
import { fmtDateTime } from '../../lib/format';

export function TransferLogsPage() {
  const [days, setDays] = useState(90);

  const { data: logs } = useQuery<any[]>({
    queryKey: ['transfer-logs', days],
    queryFn: () => $.get(`/admin/transfer-logs?days=${days}`),
  });
  const { data: offboarded } = useQuery<any[]>({
    queryKey: ['offboarded-users'],
    queryFn: () => $.get('/admin/offboarded-users'),
  });
  const { data: usersResp } = useQuery<any>({
    queryKey: ['users-for-logs'],
    queryFn: () => $.get('/users?pageSize=200'),
  });
  const userMap = new Map<number, { realName: string; avatarUrl: string | null }>();
  (usersResp?.items ?? usersResp ?? []).forEach((u: any) =>
    userMap.set(Number(u.id), { realName: u.realName, avatarUrl: u.avatarUrl ?? null }),
  );
  const UserChip = ({ id }: { id: number | null | undefined }) => {
    if (!id) return <span style={{ color: 'var(--text-3)' }}>—</span>;
    const u = userMap.get(Number(id));
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Avatar userId={id} src={u?.avatarUrl ?? null} name={u?.realName ?? `#${id}`} size="sm" />
        <span>{u?.realName ?? `#${id}`}</span>
      </span>
    );
  };

  // 按 fromUserId 聚合
  const byFrom = new Map<number, any[]>();
  for (const l of logs ?? []) {
    const k = Number(l.fromUserId);
    if (!byFrom.has(k)) byFrom.set(k, []);
    byFrom.get(k)!.push(l);
  }

  return (
    <>
      <PageHeader
        breadcrumb={['Admin', '离职转移日志']}
        title="📜 离职转移日志"
        subtitle="所有 P3-6 离职继承操作的审计记录"
      />
      <div className="work-content">
        <div style={{ marginBottom: 12 }}>
          <span style={{ marginRight: 8 }}>查看最近</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                  style={{ padding: 4 }}>
            <option value={30}>30 天</option>
            <option value={90}>90 天</option>
            <option value={365}>1 年</option>
          </select>
        </div>

        <HomeCard title={`离职员工（${offboarded?.length ?? 0}）`}>
          <TableWrap>
            <table className="ck-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>姓名</th>
                  <th>角色</th>
                  <th>离职时间</th>
                  <th>理由</th>
                  <th>继任者</th>
                  <th>转移完成时间</th>
                </tr>
              </thead>
              <tbody>
                {(offboarded ?? []).length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>暂无离职记录</td></tr>
                )}
                {(offboarded ?? []).map((u: any) => {
                  const cnt = byFrom.get(Number(u.id))?.length ?? 0;
                  return (
                    <tr key={u.id}>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <Avatar userId={u.id} src={u.avatarUrl} name={u.realName} size="sm" />
                          <span>{u.realName}</span>
                        </span>
                      </td>
                      <td><Pill tone="default">{u.role}</Pill></td>
                      <td>{fmtDateTime(u.leftAt)}</td>
                      <td style={{ maxWidth: 200, whiteSpace: 'normal' }}>{u.leftReason ?? '—'}</td>
                      <td>
                        {u.successorUserId
                          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <UserChip id={u.successorUserId} />
                              <span style={{ color: 'var(--text-3)' }}>（{cnt} 客户）</span>
                            </span>
                          : `按规则分（${cnt} 客户）`}
                      </td>
                      <td>{fmtDateTime(u.transferCompletedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        </HomeCard>

        <HomeCard title={`明细日志（${logs?.length ?? 0}）`}>
          <TableWrap>
            <table className="ck-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>时间</th>
                  <th>类型</th>
                  <th>实体 ID</th>
                  <th>从</th>
                  <th>到</th>
                  <th>执行人</th>
                  <th>理由</th>
                </tr>
              </thead>
              <tbody>
                {(logs ?? []).length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>无记录</td></tr>
                )}
                {(logs ?? []).map((l: any) => (
                  <tr key={l.id}>
                    <td>{fmtDateTime(l.executedAt)}</td>
                    <td><Pill tone="default">{l.transferType}</Pill></td>
                    <td>#{l.entityId}</td>
                    <td><UserChip id={l.fromUserId} /></td>
                    <td><UserChip id={l.toUserId} /></td>
                    <td><UserChip id={l.executedByUserId} /></td>
                    <td style={{ maxWidth: 200, whiteSpace: 'normal' }}>{l.reason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </HomeCard>
      </div>
    </>
  );
}
